/**
 * @file routes/_app/audit-tasks/$taskId.form.$instanceId.tsx
 * @description 稽核任務的查核表填寫頁。
 *
 * 流程：
 *  1. 拉取 form_instance（含 templateVersionId）
 *  2. 拉取對應 form_template_versions 的 schemaJson
 *  3. 用 packages/forms 的 Renderer 渲染 schema + data
 *  4. 用戶填寫 → 「儲存草稿」 PATCH /api/form-instances/:id（status='draft', data=...）
 *  5. 「提交」 PATCH 一併設 status='submitted'
 *
 * status='locked' 進入唯讀模式（disabled）。
 */

import { createFileRoute, useNavigate, useRouter } from '@tanstack/react-router';
import { Alert, Button, Card, Space, Tag, Tooltip, Typography } from 'antd';
import { message } from '@/lib/antd-app';
import { ArrowLeft, Download, Printer, Save, Send, WifiOff } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AUDIT_TASK_PHASES, CONTRACT_REVIEW_FORM_TEMPLATE_KEYS } from '@zhcb/shared';
import type { AuditTaskPhase } from '@zhcb/shared';
import type { FormSchema, UploadHandlers, UploadedFileMeta } from '@zhcb/forms/browser';
import { Renderer, collectFileIds, replaceFileIdInData } from '@zhcb/forms/browser';
import { PageShell } from '@/components/shell';
import { parseFieldTab, type FieldTab } from '@/components/audit-tasks/phases/field-tabs';
import {
  prefetchFormInstance,
  getPendingDraftPayload,
  saveFormInstanceOffline,
} from '@/pwa/form-instance-offline';
import { useOfflineState } from '@/pwa/use-offline-state';
import { useOfflineFocusMode } from '@/hooks/useOfflineFocusMode';
import { useResponsive } from '@/hooks/useResponsive';
import { useFieldWriteAccess } from '@/hooks/useFieldWriteAccess';
import { useFileMetas } from '@/hooks/useFileMetas';
import { uploadFile } from '@/lib/file-upload';
import { stashOfflineBlob } from '@/pwa/pending-uploads';
import { flushPendingUploads, startPendingUploadWorker } from '@/pwa/pending-upload-worker';
import { getOfflineDb } from '@/pwa/db';
import { resolveFileBlobUrl } from '@/pwa/file-blob-url';
import { fetchWithOfflineCache } from '@/pwa/offline-api-cache';
import { fetchFormPdfBlob, printPdfBlob, triggerPdfDownload } from '@/lib/form-export';
import { describeInvalidFields } from '@/lib/form-validation-message';

export const Route = createFileRoute('/_app/audit-tasks/$taskId/form/$instanceId')({
  component: FormFillPage,
  /**
   * `?from=` 記錄「從任務詳情哪個階段」進入本表單頁（客戶 PPT S48：
   * 填完點「返回任務」須回到進入前的階段位置，而非丟失上下文）。
   * 僅接受 6 個合法階段值，非法值折疊為 undefined（此時返回按鈕回退瀏覽器歷史）。
   */
  validateSearch: (
    search: Record<string, unknown>,
  ): { from?: AuditTaskPhase; fromTab?: FieldTab } => ({
    from: AUDIT_TASK_PHASES.includes(search.from as AuditTaskPhase)
      ? (search.from as AuditTaskPhase)
      : undefined,
    // 序21：來源頁籤（現場稽核的 稽核文件包 / 採樣送檢 …），返回任務時據此定位
    fromTab: parseFieldTab(search.fromTab),
  }),
});

interface FormInstance {
  id: number;
  templateId: number;
  templateVersionId: number;
  ownerType: string | null;
  ownerId: number | null;
  data: Record<string, unknown>;
  status: 'draft' | 'submitted' | 'locked';
  version: number;
  createdAt: string;
  updatedAt: string;
}

interface FormTemplateVersion {
  id: number;
  templateId: number;
  version: number;
  schemaJson: FormSchema;
  createdAt: string;
}

interface FormTemplate {
  id: number;
  code: string;
  /** 系統識別鍵（序40：客戶改過代碼後仍以此配對；null 回落 code） */
  systemKey?: string | null;
  name: string;
}

function FormFillPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const { taskId, instanceId } = Route.useParams();
  /** 來源階段：任務詳情各階段的表單入口跳轉時帶上（如 PhaseField 帶 `?from=field`） */
  const { from, fromTab } = Route.useSearch();
  const qc = useQueryClient();
  /** 離線狀態（網絡 + outbox 待同步數），用於顯示橫幅與 badge */
  const { online, pending, triggerSync, syncing } = useOfflineState();
  /** 離線現場專注模式：返回任務時須固定回 `?phase=field`（CLAUDE.md §10 允許停留路徑） */
  const { focusMode } = useOfflineFocusMode();
  /** Pad 響應式：表單容器寬度自適應 */
  const { isMobile } = useResponsive();
  /**
   * 序61：管理組（manager）現場作業唯讀——無現場寫入能力時，查核表以唯讀模式渲染
   * （隱藏儲存/提交按鈕、Renderer disabled），只供瀏覽已填內容。
   */
  const { canFieldWrite } = useFieldWriteAccess();

  /**
   * 「返回任務」：回到進入前的任務詳情位置（含所在階段），而不是丟失上下文。
   *
   * 優先級：
   *  1. 離線 / 現場專注模式：固定回 `?phase=field`——這是離線工作區的允許停留路徑，
   *     且保持現場稽核專注殼不變（CLAUDE.md §10）。
   *  2. 有 `?from=<phase>` 來源階段：回 `/audit-tasks/$taskId?phase=<from>`，
   *     任務詳情頁會以該階段作為初始定位。
   *  3. 無來源資訊（直接輸入網址等）：回退瀏覽器歷史；無歷史可退時回任務詳情預設頁。
   */
  const handleBackToTask = () => {
    // 序21：現場稽核階段一併帶回來源頁籤（如 稽核文件包），避免落回預設的「打卡」頁籤
    if (focusMode || !online) {
      void navigate({
        to: '/audit-tasks/$taskId',
        params: { taskId },
        search: { phase: 'field', tab: fromTab },
      });
      return;
    }
    if (from) {
      void navigate({
        to: '/audit-tasks/$taskId',
        params: { taskId },
        search: { phase: from, tab: from === 'field' ? fromTab : undefined },
      });
      return;
    }
    if (window.history.length > 1) {
      router.history.back();
      return;
    }
    void navigate({
      to: '/audit-tasks/$taskId',
      params: { taskId },
      search: { phase: undefined },
    });
  };

  // 拉表單實例
  const { data: instData, isLoading: instLoading } = useQuery<{ instance: FormInstance }>({
    queryKey: ['form-instance', Number(instanceId)],
    queryFn: async () => {
      const res = await fetchWithOfflineCache<{ instance: FormInstance }>(
        `/api/form-instances/${instanceId}`,
        { taskId: Number(taskId), scope: 'form-instance' },
      );
      return res.data;
    },
  });
  const instance = instData?.instance;

  // 拉模板（為了顯示名稱與 schemaJson；後端目前只能透過 GET /api/form-templates/:id 取得 versions）
  const { data: tplData, isLoading: tplLoading } = useQuery<{
    template: FormTemplate;
    versions: FormTemplateVersion[];
  }>({
    queryKey: ['form-template', instance?.templateId],
    queryFn: async () => {
      const res = await fetchWithOfflineCache<{
        template: FormTemplate;
        versions: FormTemplateVersion[];
      }>(`/api/form-templates/${instance!.templateId}`, {
        taskId: Number(taskId),
        scope: 'form-template',
      });
      return res.data;
    },
    enabled: !!instance?.templateId,
  });

  const versionRow = tplData?.versions.find((v) => v.id === instance?.templateVersionId);
  const schema = versionRow?.schemaJson;

  /**
   * 序228 增強：versions 已按版本號倒序回傳，[0] 即模板目前最新版本。
   * 與本實例當初凍結的 versionRow 比對版本號，純告知，不提供切換（切換涉及資料遷移，不在此範圍）。
   */
  const latestVersionRow = tplData?.versions[0];
  const hasNewerVersion = !!(
    latestVersionRow &&
    versionRow &&
    latestVersionRow.version > versionRow.version
  );

  // 本地表單資料
  const [localData, setLocalData] = useState<Record<string, unknown>>({});
  const localDataRef = useRef<Record<string, unknown>>({});
  const [dirty, setDirty] = useState(false);
  const [savingStatus, setSavingStatus] = useState<'draft' | 'submitted' | null>(null);
  /**
   * 導出中的操作類型（客戶 PPT S49②，序4）：控制「下載 PDF / 列印」按鈕 loading，
   * 避免重複點擊。僅在線時可用（離線由 `online` 判斷禁用）。
   */
  const [exportAction, setExportAction] = useState<'download' | 'print' | null>(null);

  useEffect(() => {
    if (instance) {
      const nextData = instance.data ?? {};
      localDataRef.current = nextData;
      setLocalData(nextData);
      setDirty(false);
      void getPendingDraftPayload(Number(instanceId)).then((pendingPayload) => {
        if (pendingPayload?.data) {
          localDataRef.current = pendingPayload.data;
          setLocalData(pendingPayload.data);
          setDirty(true);
        }
      });
    }
  }, [instance, instanceId]);

  /**
   * 進入頁面時預熱：把 instance + template 拉一次，讓 SW runtimeCaching 入庫；
   * schemaJson 同時寫進 IndexedDB cache_forms 作為二級兜底。
   * Best-effort，失敗靜默吞下。
   */
  useEffect(() => {
    void prefetchFormInstance(Number(instanceId));
  }, [instanceId]);

  /**
   * 啟動 pending-upload-worker：
   *  - startPendingUploadWorker 註冊全域 online 監聽器（內部會 idempotent，不會重複註冊）
   *  - 進頁面立即跑一次 flushPendingUploads，作為前一次會話遺留 blob 的安全網
   * deps=[] 確保只跑一次（每次掛載）。
   */
  useEffect(() => {
    startPendingUploadWorker();
    void flushPendingUploads();
  }, []);

  /**
   * 監聽 pending-upload-worker 完成時派發的 `pending-upload:replaced` 事件，
   * 把 React state 中對應的負數 tempId 同步替換成正數 realId（schema 驅動）。
   * 否則使用者下一次儲存草稿會把舊的負數寫回 outbox，造成迴圈。
   * 依賴 schema：handler 內部呼叫 replaceFileIdInData(schema, ...)，schema 變動需重新綁定。
   */
  useEffect(() => {
    if (!schema) return;
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ tempId: number; realId: number }>;
      setLocalData((prev) => {
        const next = replaceFileIdInData(schema, prev, ce.detail.tempId, ce.detail.realId);
        localDataRef.current = next;
        return next;
      });
    };
    window.addEventListener('pending-upload:replaced', handler);
    return () => window.removeEventListener('pending-upload:replaced', handler);
  }, [schema]);

  const isLocked = instance?.status === 'locked';
  const isSubmitted = instance?.status === 'submitted';
  /**
   * 序61：唯讀 = 已鎖定 或 無現場寫入能力（如管理組）。
   * 用於隱藏儲存/提交按鈕並讓 Renderer disabled（仍可瀏覽已填內容、下載/列印 PDF）。
   */
  /**
   * 序76：合約評審表（B-ZHSR01-12 / B-ZHSR01-3）內容一律同步自派案專區「合約評審」，
   * 單一資料源、不在此重新輸入 → 唯讀並提示來源。
   */
  const tplKey = tplData?.template.systemKey ?? tplData?.template.code ?? '';
  const isContractReviewForm = CONTRACT_REVIEW_FORM_TEMPLATE_KEYS.includes(tplKey);
  const readOnly = isLocked || !canFieldWrite || isContractReviewForm;

  /**
   * 表單儲存（線上直 PATCH，離線/失敗自動入 outbox 待同步）。
   *
   * 行為：
   * - 線上：apiClient.patch 成功後刷新 React Query 快取，與舊行為一致
   * - 離線：saveFormInstanceOffline 內部把 PATCH 進 outbox（per-instance upsert）
   *   返回 { offline: true }，前端 toast「已存到本地，待聯網同步」並仍清除 dirty
   * - 4xx 業務錯誤（含 409 衝突）：照舊提示，不入 outbox
   */
  const handleSave = async (status: 'draft' | 'submitted') => {
    if (savingStatus) return;
    setSavingStatus(status);
    try {
      const payload = { data: localDataRef.current, status, version: instance?.version };
      // 若 attachments 中含負數 tempId（離線暫存的 blob），強制走 outbox 路徑：
      // 這些 fileId 在伺服器上不存在，必須等 pending-upload-worker 換成正數後再 PATCH。
      const hasOfflineAttachments =
        !!schema && collectFileIds(schema, payload.data ?? {}).some((id) => id < 0);
      const result = await saveFormInstanceOffline(Number(instanceId), payload, {
        forceOutbox: hasOfflineAttachments,
      });

      // 線上路徑：刷新查詢快取（離線路徑無新資料，不需要 invalidate）
      if (!result.offline) {
        void qc.invalidateQueries({ queryKey: ['form-instance', Number(instanceId)] });
        void qc.invalidateQueries({ queryKey: ['audit-task-detail', Number(taskId)] });
      }
      setDirty(false);
      if (result.offline) {
        message.warning(
          status === 'submitted' ? '已存到本地，待聯網後自動提交' : '已存到本地，待聯網後自動同步',
        );
      } else if (status === 'submitted') {
        message.success('表單已提交');
      } else {
        message.success('草稿已儲存');
      }
    } catch (err) {
      const error = err as { status?: number; message?: string; details?: unknown };
      if (error.status === 400) {
        // 序49：指出是哪些欄位未通過（草稿只做格式校驗，不再要求必填）；提交時才要求必填
        const labels = describeInvalidFields(schema, error.details);
        const prefix =
          status === 'submitted' ? '提交前請完成必填欄位並修正格式' : '以下欄位格式不正確';
        message.error(
          labels.length ? `${prefix}：${labels.join('、')}` : '部分欄位未通過校驗，請檢查表單',
        );
      } else if (error.status === 409) {
        message.error('資料已被他人更新，請重新整理');
      } else if (error.status === 422) {
        message.error(error.message ?? '表單已鎖定');
      } else {
        message.error('儲存失敗');
      }
    } finally {
      setSavingStatus(null);
    }
  };

  const handleChange = (next: Record<string, unknown>) => {
    localDataRef.current = next;
    setLocalData(next);
    setDirty(true);
  };

  // ── 收集表单中所有引用的 fileId（按 schema 遍历），批量解析元信息 ──
  // - 仅当 schema + localData 都就绪时才计算（避免空数组重取）
  // - useFileMetas 自己会过滤负数 / 去重 / 不发请求当 ids 空
  const fileIds = useMemo<number[]>(() => {
    if (!schema) return [];
    return collectFileIds(schema, localData);
  }, [schema, localData]);
  const { metas: fileMetas } = useFileMetas(fileIds);

  /**
   * 上传通道：把 packages/forms 内部 Upload 控件的 File 直传到 OSS。
   *
   * - scope='form-attachment'：表单附件专属 scope，OSS 路径前缀为 form-attachment/YYYY-MM-DD/...
   * - ownerType='form_instance' / ownerId=instanceId：让后端 files 表能反查归属
   * - fileMetas：由 useFileMetas 批量解析得到，刷新后仍可显示真实文件名
   * - onRemoveFile：阶段 1 暂不实现（同一 fileId 可能被多处引用，不调 DELETE /api/files/:id）
   * - 失败由 ChecklistField/FieldFactory 自己 toast，这里只把 Promise reject 抛上去
   */
  const uploadHandlers: UploadHandlers = useMemo(
    () => ({
      onUploadFile: async (file: File): Promise<UploadedFileMeta> => {
        // 离线分支：把 Blob 暂存到 IndexedDB（cache_media + meta），分配负数 tempId
        // 立即返回真實 filename/size/mime，讓上傳控件無感切換；正數 id 由 worker 聯網後補齊。
        if (typeof navigator !== 'undefined' && !navigator.onLine) {
          const tempId = await stashOfflineBlob(file, {
            ownerType: 'form_instance',
            ownerId: Number(instanceId),
            scope: 'form-attachment',
          });
          return {
            id: tempId,
            filename: file.name,
            sizeBytes: file.size,
            mimeType: file.type || 'application/octet-stream',
          };
        }
        // 在线分支：直传 OSS（與舊行為一致）
        const uploaded = await uploadFile(file, {
          scope: 'form-attachment',
          ownerType: 'form_instance',
          ownerId: Number(instanceId),
        });
        return {
          id: uploaded.id,
          filename: uploaded.filename,
          sizeBytes: uploaded.sizeBytes,
          mimeType: uploaded.mimeType,
        };
      },
      fileMetas,
      // 阶段 3：fileId → blob URL 解析（cache_media 优先，未命中且在线时 fallback OSS 预签名）
      resolveBlobUrl: resolveFileBlobUrl,
      // onRemoveFile 暂不实现：阶段 1 不调 DELETE /api/files/:id（文件可能多处引用）
    }),
    [instanceId, fileMetas],
  );

  /**
   * 失敗附件計數：cache_media 中 status='error' 的列數。
   * - 進頁面立即查一次
   * - 監聽 'pending-upload:replaced'（成功一筆就重查，可能影響 error 計數）
   * - 30s 輪詢一次（覆蓋背景任務改動 status 但未派發事件的場景）
   */
  const [erroredCount, setErroredCount] = useState(0);
  const refreshErroredCount = useCallback(async () => {
    const db = await getOfflineDb();
    const errored = await db.getAllFromIndex('cache_media', 'by-status', 'error');
    setErroredCount(errored.length);
  }, []);
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const db = await getOfflineDb();
      const errored = await db.getAllFromIndex('cache_media', 'by-status', 'error');
      if (!cancelled) setErroredCount(errored.length);
    };
    void refresh();
    const handler = () => void refresh();
    window.addEventListener('pending-upload:replaced', handler);
    const t = setInterval(() => void refresh(), 30_000);
    return () => {
      cancelled = true;
      window.removeEventListener('pending-upload:replaced', handler);
      clearInterval(t);
    };
  }, []);

  const handleSaveDraft = () => {
    void handleSave('draft');
  };

  const handleSubmit = () => {
    void handleSave('submitted');
  };

  /**
   * 下載 PDF / 列印（客戶 PPT S49②，序4）：拉取後端導出的 PDF blob 後按操作分發。
   * - 僅在線可用（離線時按鈕禁用，此處為雙保險 early-return）。
   * - 導出反映「當前已保存」的內容；若有未保存草稿，提示先儲存。
   * - 下載檔名：`表單名-任務編號.pdf`（任務編號優先 planCode 未取得時回落 instanceId）。
   */
  const handleExport = async (action: 'download' | 'print') => {
    if (exportAction) return; // 已有導出進行中，忽略重複點擊
    if (!online) {
      message.warning('離線狀態無法下載/列印，請恢復網絡後操作');
      return;
    }
    if (dirty) {
      message.warning('有未儲存的變更，請先「儲存草稿」再下載/列印');
      return;
    }
    setExportAction(action);
    try {
      const blob = await fetchFormPdfBlob(Number(instanceId));
      if (action === 'download') {
        const name = tplData?.template.name ?? '查核表';
        triggerPdfDownload(blob, `${name}-${taskId}.pdf`);
        message.success('PDF 已開始下載');
      } else {
        printPdfBlob(blob, (err) => {
          console.error('[FormFillPage] print form pdf failed', err);
          message.error('喚起列印失敗，請改用「下載 PDF」後手動列印');
        });
      }
    } catch (err) {
      console.error('[FormFillPage] export form pdf failed', { instanceId, action, err });
      message.error(
        `${action === 'download' ? '下載' : '列印'}失敗：${err instanceof Error ? err.message : '未知錯誤'}`,
      );
    } finally {
      setExportAction(null);
    }
  };

  const isLoading = instLoading || tplLoading;

  return (
    <PageShell
      title={
        <Space size={12}>
          <span>{tplData?.template.name ?? '查核表填寫'}</span>
          {instance && (
            <Tag
              color={
                instance.status === 'locked'
                  ? 'success'
                  : instance.status === 'submitted'
                    ? 'processing'
                    : 'default'
              }
            >
              {instance.status === 'locked'
                ? '已鎖定'
                : instance.status === 'submitted'
                  ? '已提交'
                  : '草稿'}
            </Tag>
          )}
        </Space>
      }
      actions={
        <Space wrap>
          <Button onClick={handleBackToTask} icon={<ArrowLeft size={14} />} block={isMobile}>
            返回任務
          </Button>
          {/* 下載 PDF / 列印（客戶 PPT S49②，序4）：僅在線可用，離線置灰並 Tooltip 提示 */}
          <Tooltip title={online ? undefined : '離線狀態無法下載，請恢復網絡後操作'}>
            <Button
              icon={<Download size={14} />}
              onClick={() => void handleExport('download')}
              disabled={!online || exportAction !== null}
              loading={exportAction === 'download'}
              block={isMobile}
            >
              下載 PDF
            </Button>
          </Tooltip>
          <Tooltip title={online ? undefined : '離線狀態無法列印，請恢復網絡後操作'}>
            <Button
              icon={<Printer size={14} />}
              onClick={() => void handleExport('print')}
              disabled={!online || exportAction !== null}
              loading={exportAction === 'print'}
              block={isMobile}
            >
              列印
            </Button>
          </Tooltip>
          {/* 序61：唯讀（已鎖定或管理組）不顯示儲存/提交，只保留返回/下載/列印 */}
          {!readOnly && (
            <>
              <Button
                icon={<Save size={14} />}
                onClick={handleSaveDraft}
                loading={savingStatus === 'draft'}
                disabled={!dirty || savingStatus !== null}
                block={isMobile}
              >
                儲存草稿
              </Button>
              <Button
                type="primary"
                icon={<Send size={14} />}
                onClick={handleSubmit}
                loading={savingStatus === 'submitted'}
                disabled={savingStatus !== null}
                block={isMobile}
              >
                {isSubmitted ? '重新提交' : '提交'}
              </Button>
            </>
          )}
        </Space>
      }
    >
      {/* 離線/待同步橫幅：當前離線 或 outbox 有 pending 條目 時顯示 */}
      {(!online || pending > 0) && (
        <Alert
          style={{ marginBottom: 12 }}
          type={online ? 'info' : 'warning'}
          showIcon
          icon={<WifiOff size={16} />}
          message={
            online
              ? `已恢復網路，正在同步 ${pending} 筆離線變更…`
              : '目前離線，所有儲存將先存到本地，待聯網後自動同步'
          }
          description={
            pending > 0 ? (
              <Space>
                <span>本地待同步：{pending} 筆</span>
                <Button size="small" onClick={() => void triggerSync()} loading={syncing}>
                  立即同步
                </Button>
              </Space>
            ) : undefined
          }
        />
      )}

      {/* 序76：合約評審表資料來源提示（唯讀，修改請至合約評審） */}
      {isContractReviewForm && (
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message="本表單內容自動同步自「派案專區 → 合約評審」"
          description="合約評審完成後資料會自動帶入，無需重新輸入；如需修改請至派案專區的合約評審辦理。"
        />
      )}

      {/* 表單模板已有新版本告警（序228 增強）：僅告知，不提供切換（涉及已填資料遷移，不在此範圍） */}
      {hasNewerVersion && latestVersionRow && (
        <Alert
          style={{ marginBottom: 12 }}
          type="info"
          showIcon
          message={`此表單已有新版本（v${latestVersionRow.version}，更新於 ${new Date(
            latestVersionRow.createdAt,
          ).toLocaleDateString('zh-TW')}）`}
          description="目前顯示的仍是建立任務時凍結的舊版本內容，如需使用新版本請聯繫管理組。"
        />
      )}

      {/* 附件上傳失敗告警：cache_media 中 status='error' 的條目；提供「立即重試」手動觸發 worker */}
      {erroredCount > 0 && (
        <Alert
          style={{ marginBottom: 12 }}
          type="error"
          showIcon
          message={`${erroredCount} 个附件上传失败`}
          description={
            <Space>
              <span>请检查网络后重试，或联系管理员。</span>
              <Button
                size="small"
                onClick={() => {
                  // flushPendingUploads 返回 void、内部吞掉每行的失败，
                  // 不能直接 setErroredCount(0)（PR #2 review #8）；
                  // 改为重读真实 error 行数。
                  void flushPendingUploads().then(() => void refreshErroredCount());
                }}
              >
                立即重試
              </Button>
            </Space>
          }
        />
      )}

      {isLoading ? (
        <Card>
          <Typography.Text>載入中…</Typography.Text>
        </Card>
      ) : !instance ? (
        <Alert type="error" message="表單實例不存在" />
      ) : !schema ? (
        <Alert type="error" message="表單版本缺失或無 schema 定義" />
      ) : (
        /**
         * 表單卡片佔滿可用寬度（現場填寫需要盡量大的輸入區）。
         */
        <>
          <Card style={{ width: '100%' }}>
            <Renderer
              schema={schema}
              value={localData}
              onChange={handleChange}
              disabled={readOnly}
              uploadHandlers={uploadHandlers}
            />
          </Card>
          {/*
           * 2026-09-15 回報序32：表單頁面底部同樣提供「返回任務 / 儲存草稿 / 提交」，
           * 長表單填完不必捲回頂部操作。所有電子表單皆經本頁填寫，故一處修改即全數套用。
           * 按鈕狀態與頂部一致（唯讀時僅保留返回任務）。
           */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
              gap: 8,
              marginTop: 16,
            }}
          >
            <Button onClick={handleBackToTask} icon={<ArrowLeft size={14} />} block={isMobile}>
              返回任務
            </Button>
            {!readOnly && (
              <>
                <Button
                  icon={<Save size={14} />}
                  onClick={handleSaveDraft}
                  loading={savingStatus === 'draft'}
                  disabled={!dirty || savingStatus !== null}
                  block={isMobile}
                >
                  儲存草稿
                </Button>
                <Button
                  type="primary"
                  icon={<Send size={14} />}
                  onClick={handleSubmit}
                  loading={savingStatus === 'submitted'}
                  disabled={savingStatus !== null}
                  block={isMobile}
                >
                  {isSubmitted ? '重新提交' : '提交'}
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </PageShell>
  );
}
